<script setup lang="ts">
/* =============================================================
   USER MENU
   =============================================================
   The signed-in identity in the top bar: avatar, name, company —
   and a dropdown for account actions. Sign out also exists as a
   standalone button next to it, so it is always one click away.
   ============================================================= */

import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Avatar from '@/components/ui/Avatar.vue'
import { appConfig } from '@/config/app.config'
import { routes } from '@/config/routes'
import { useOnClickOutside } from '@/lib/composables/useOnClickOutside'
import type { AuthUser } from '@/lib/auth/types'

const props = defineProps<{ user: AuthUser }>()
const emit = defineEmits<{ signOut: [] }>()

const container = ref<HTMLElement | null>(null)
const open = ref(false)

function close() {
  open.value = false
}

useOnClickOutside(container, close, open)

const roleLabel = computed(
  () => appConfig.roles.find((role) => role.value === props.user.role)?.label ?? 'Account',
)

function signOut() {
  close()
  emit('signOut')
}
</script>

<template>
  <div ref="container" class="wrap">
    <button
      type="button"
      class="trigger"
      :aria-expanded="open"
      aria-haspopup="menu"
      @click="open = !open"
    >
      <Avatar :name="user.name" :src="user.avatarUrl" :size="32" />
      <span class="identity">
        <span class="name">{{ user.name }}</span>
        <span class="company">{{ user.company }}</span>
      </span>
      <Icon name="chevronDown" :size="16" class="chevron" />
    </button>

    <div v-if="open" class="menu" role="menu">
      <div class="menuHeader">
        <Avatar :name="user.name" :src="user.avatarUrl" :size="36" />
        <div class="menuIdentity">
          <span class="name">{{ user.name }}</span>
          <span class="email">{{ user.email }}</span>
        </div>
      </div>

      <dl class="meta">
        <div class="metaRow">
          <dt>Company</dt>
          <dd>{{ user.company }}</dd>
        </div>
        <div class="metaRow">
          <dt>Account type</dt>
          <dd>{{ roleLabel }}</dd>
        </div>
      </dl>

      <div class="menuActions">
        <RouterLink :to="routes.settings" class="menuItem" role="menuitem" @click="close">
          <Icon name="settings" :size="16" />
          Account settings
        </RouterLink>
        <button type="button" class="menuItem menuItemDanger" role="menuitem" @click="signOut">
          <Icon name="logout" :size="16" />
          Sign out
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wrap {
  position: relative;
}

.trigger {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);
  max-width: 15rem;
  padding: var(--space-3xs) var(--space-2xs);
  background: none;
  border: var(--border-width) solid transparent;
  border-radius: var(--radius-sm);
  transition:
    background-color var(--dur-fast) var(--ease),
    border-color var(--dur-fast) var(--ease);
}

.trigger:hover,
.trigger[aria-expanded='true'] {
  background-color: var(--color-surface-sunken);
  border-color: var(--color-border);
}

.identity {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  line-height: var(--leading-tight);
  text-align: left;
}

.name {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 11rem;
}

.company,
.email {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 11rem;
}

.chevron {
  flex: none;
  color: var(--color-text-faint);
}

/* --- dropdown --------------------------------------------------- */

.menu {
  position: absolute;
  top: calc(100% + var(--space-3xs));
  inset-inline-end: 0;
  z-index: var(--z-overlay);
  width: 17rem;
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: drop var(--dur-fast) var(--ease);
}

@keyframes drop {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
}

.menuHeader {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);
  padding: var(--space-sm);
  border-bottom: var(--border-width) solid var(--color-border);
}

.menuIdentity {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: var(--leading-tight);
}

.meta {
  margin: 0;
  padding: var(--space-2xs) var(--space-sm);
  border-bottom: var(--border-width) solid var(--color-border);
}

.metaRow {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2xs);
  padding-block: var(--space-3xs);
  font-size: var(--text-xs);
}

.metaRow dt {
  color: var(--color-text-faint);
}

.metaRow dd {
  margin: 0;
  font-weight: var(--weight-medium);
  color: var(--color-text);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menuActions {
  padding: var(--space-3xs);
}

.menuItem {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);
  width: 100%;
  padding: var(--space-2xs) var(--space-xs);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-text);
  text-align: left;
  text-decoration: none;
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  transition:
    background-color var(--dur-fast) var(--ease),
    color var(--dur-fast) var(--ease);
}

.menuItem:hover {
  background-color: var(--color-surface-sunken);
  color: var(--color-heading);
}

.menuItemDanger {
  color: var(--color-danger);
}

.menuItemDanger:hover {
  color: var(--color-danger);
  background-color: var(--color-danger-soft);
}

@media (max-width: 56.25rem) {
  .identity,
  .chevron {
    display: none;
  }

  .trigger {
    padding: var(--space-3xs);
  }
}
</style>
